import { ProcessVisitorResponse } from '../../types'
import { COOKIE_USER_ID, COOKIE_USER_LEVEL } from '../constants'
import { AppStateType, SecureStateType } from '../constants/enums'
import { updateAppState } from '../state/state.app'
import {
    AgeVerificationProvider,
    AgeVerificationState,
    updateSecureState,
} from '../state/state.secure'
import { setCookie } from '../utilities/cookies'
import { safeObj } from '../utilities/safe'
import { fetchEntryInformation } from './api/account'

/** Fetch visitor information and update state */
export const getVisitorInformation = async (result: ProcessVisitorResponse) => {
    if (!result || typeof result?.age_check_required === 'undefined') {
        try {
            const accountDetails = await fetchEntryInformation()
            const { result: processVisitorResult } = safeObj(accountDetails)

            if (!processVisitorResult) {
                return
            }

            result = processVisitorResult
        } catch (error) {
            console.error(error)
            return
        }
    }

    const {
        user,
        age_check_required,
        age_check_services,
        cookies,
        maintenance_mode,
        redirection,
    } = result

    const level = user?.access_level || 0
    const token = user?.token
    const id = user?.id || 0

    setCookie(COOKIE_USER_LEVEL, level + '')
    setCookie(COOKIE_USER_ID, id + '')

    // dynamically handle age verification providers
    const providers = Object.keys(age_check_services || {})
    const serviceProviders: Record<string, AgeVerificationProvider> = {}
    providers.forEach((provider) => {
        serviceProviders[provider] = {
            perks: age_check_services[provider]?.perks,
            enabled: age_check_services[provider]?.enabled === 1,
            url: age_check_services[provider]?.url,
            region: age_check_services[provider]?.region,
            countryCode: age_check_services[provider]?.country_code,
        }
    })

    // yoti is allowed by default for now; however ondato is conditional
    if (providers.length === 0) {
        serviceProviders['yoti'] = {
            enabled: true,
            url: 'https://bouncer.myfreecams.com/',
        }
    }

    if (redirection && redirection.startsWith('https://')) {
        // if a user is forcibly redirected from MFC App
        window.location.replace(redirection)
    }

    // update cookies
    if (cookies && Array.isArray(cookies) && cookies?.length > 0) {
        cookies.forEach((cookieData) => {
            const cookieKey = cookieData[0].toString()
            const cookieValue = cookieData[1].toString()
            const expiration = new Date((cookieData[2] as number) * 1000)
            setCookie(cookieKey, cookieValue, {
                expires: expiration,
            })
        })
    }

    const secureState: AgeVerificationState = {
        ...(age_check_required === 0
            ? { ageGateActive: false }
            : {
                  ageVerificationProviders: serviceProviders,
                  ageGateActive: true,
              }),
    }

    updateSecureState(SecureStateType.AGE_VERIFICATION, secureState)

    const appState = { maintenanceMode: maintenance_mode === 1 }

    updateAppState(AppStateType.APP, appState)

    const sessionState = { token }

    if (token) {
        updateAppState(AppStateType.SESSION, sessionState)
    }

    return {
        user: { level, id, ...(user ?? {}) },
        ...sessionState,
        ...secureState,
        ...appState,
    }
}
